import Logger from "~/lib/Logger";

import Icon from "~/components/shared/Icon";
import { BuildStates, isTerminalBuildState } from "~/constants/BuildStates";
import { twMerge } from "tailwind-merge";
import { JobStates } from "~/components/build/Show/lib/types";

const VARIANTS = {
  tiny: "tiny",
  large: "large",
} as const;

type Variant = keyof typeof VARIANTS;

type Build = {
  state: BuildStates;
};

type Job = {
  type: string;
  state: JobStates;
  passed?: boolean | null;
  softFailed?: boolean | null;
  unblockedAt?: string;
  missingDependencies?: string[];
};

type Props = {
  job?: Job;
  build?: Build;
  style?: any;
  variant?: Variant;
  disableAnimation?: boolean;
  speedUpAnimationOnHover?: boolean;
  className?: string;
};

export const colorForJob = (job: Job, build: Build) => {
  if (isTerminalBuildState(build.state) && job.missingDependencies?.length) {
    return "text-red-500";
  }

  switch (job.state) {
    case "canceled":
      if (job.passed) {
        return "text-green-500";
      }
      return "text-slate-500";
    case "timed_out":
    case "finished":
      if (job.passed) {
        return "text-green-500";
      }
      return "text-red-500";
    case "running":
      return "text-orange-500";
    case "pending":
    case "waiting":
    case "waiting_failed":
      return "text-slate-500";
    default:
      return "text-charcoal-700";
  }
};

const animatedIconClassName = (
  baseIconName: string | null | undefined,
  disableAnimation: boolean,
  speedUpAnimationOnHover: boolean,
) => {
  if (disableAnimation || (baseIconName !== "progress" && baseIconName !== "clock-rainbow")) {
    return null;
  }

  return twMerge(
    "origin-center will-change-transform",
    !speedUpAnimationOnHover && "animate-pipeline-state-icon-spin",
    speedUpAnimationOnHover &&
      "animate-pipeline-state-icon-spin-hover-boost [animation-play-state:paused] group-hover:[animation-play-state:running]",
  );
};

/**
 * A wrapper on our shared Icon component that renders the correct icon for a given job or
 * build.
 *
 * @param {Job} job the job to render the icon for. Only one of `job` or `build` should be
 * provided.
 * @param {Build} build the build to render the icon for. Only one of `job` or `build`
 * should be provided.
 * @param {Variant} variant the desired size variant for this icon. Valid values are:
 * 'tiny', 'large', and may be retrieved via the VARIANTS export of the PipelineStateIcon
 * module.
 * @returns the appropriate icon for the provided job
 */
const PipelineStateIcon = ({
  job,
  build,
  style = {},
  disableAnimation = false,
  speedUpAnimationOnHover = false,
  variant = VARIANTS.tiny,
  className,
}: Props) => {
  if (!job && !build) {
    return null;
  }
  if (job && build) {
    Logger.error(
      "[PipelineStateIcon] received both `job` and `build` props. Only one of these props should be provided.",
    );
  }

  let baseIconName;
  if (job) {
    baseIconName = iconForJob(job);
  } else if (build) {
    baseIconName = iconForBuild(build, variant);
  }

  if (!baseIconName) {
    return null;
  }
  const fullIconName = `${baseIconName}-${variant}`;
  const iconClassName = twMerge(
    animatedIconClassName(baseIconName, disableAnimation, speedUpAnimationOnHover),
    className,
  );

  if (
    speedUpAnimationOnHover &&
    !disableAnimation &&
    (baseIconName === "progress" || baseIconName === "clock-rainbow")
  ) {
    return (
      <span className="animate-pipeline-state-icon-spin group inline-flex origin-center will-change-transform">
        <Icon
          icon={fullIconName}
          style={{ fill: "none", height: 14, width: 14, ...style }}
          className={iconClassName}
        />
      </span>
    );
  }

  return (
    <Icon
      icon={fullIconName}
      style={{ fill: "none", height: 14, width: 14, ...style }}
      className={iconClassName}
    />
  );
};

const iconForJob = (job: Job) => {
  if (job.softFailed) {
    return "alert";
  }

  if (job.type === "waiter") {
    return "chevron-right";
  }

  if (job.type === "manual") {
    if (job.state === "finished" && job.unblockedAt) {
      return "chevron-right";
    }
  }

  if (job.missingDependencies?.length) {
    return "alert";
  }

  switch (job.state) {
    case "pending":
    case "waiting":
    case "waiting_failed":
    case "limiting":
    case "limited":
    case "platform_limiting":
    case "platform_limited":
    case "assigned":
    case "accepted":
      return "clock";
    case "reserved":
      return "lock";
    case "blocked":
    case "blocked_failed":
    case "unblocked_failed":
      return "paused";
    case "unblocked":
      return "chevron-right";
    case "scheduled":
      return "clock";
    case "skipped":
      return "dash";
    case "running":
    case "canceling":
    case "timing_out":
      return "progress";
    case "canceled":
      return job.passed ? "check" : "dash-diagonal";
    case "finished":
    case "timed_out":
    case "expired":
      return job.passed ? "check" : "cross";
    case "broken":
      return "cross";
    default:
      return null;
  }
};

const iconForBuild = (build: Build, variant: Variant) => {
  switch (build.state) {
    case "failing":
    case "started":
    case "canceling":
    case "running":
      return "progress";
    case "failed":
      return "cross";
    case "canceled":
      return "dash-diagonal";
    case "passed":
      return "check";
    case "blocked":
      return "paused";
    case "creating":
      // This is a special case - the designs have two 'clock-large' icons, one for
      // 'scheduled' and one for 'waiting'. All other size variants for both states are
      // the same.
      return variant === VARIANTS.large ? "clock-rainbow" : "clock";
    case "scheduled":
      return "clock";
    case "skipped":
    case "not_run":
      return "dash";
    default:
      return null;
  }
};

export default PipelineStateIcon;
export { VARIANTS };
